import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import LinearProgress from "@mui/material/LinearProgress";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import ClearIcon from "@mui/icons-material/Close";

import { api, get, post } from "lib/api";
import MobileTopBar from "mobile/components/MobileTopBar";
import { IosCard, IosFilterBar, IosList, IosListItem, IosPage, IosSectionHeader, iosColors } from "mobile/components/IosParity";

const STATUS_OPTIONS = [
  { key: "all", label: "All", icon: "list_alt", accent: "orange" },
  { key: "open", label: "Open", icon: "pending_actions", accent: "orange" },
  { key: "received", label: "Received", icon: "move_to_inbox", accent: "green" },
  { key: "testing", label: "Testing", icon: "science", accent: "orange" },
  { key: "approved", label: "Approved", icon: "check_circle", accent: "green" },
  { key: "denied", label: "Denied", icon: "block", accent: "red" },
  { key: "closed", label: "Closed", icon: "verified", accent: "green" },
];

const WORKFLOW_OPTIONS = [
  { key: "all", label: "All", icon: "list_alt", accent: "orange" },
  { key: "submitted", label: "Submitted", icon: "assignment", accent: "orange" },
  { key: "awaiting_claim_decision", label: "Decision", icon: "rule", accent: "orange" },
  { key: "approved", label: "Approved", icon: "check_circle", accent: "green" },
  { key: "inbound_label_generated", label: "Label", icon: "local_shipping", accent: "green" },
  { key: "replacement_order_created", label: "Order", icon: "receipt_long", accent: "green" },
  { key: "replacement_shipped", label: "Shipped", icon: "local_shipping", accent: "green" },
  { key: "awaiting_replacement_outcome", label: "Outcome", icon: "support_agent", accent: "orange" },
  { key: "received_for_inspection", label: "Received", icon: "move_to_inbox", accent: "green" },
  { key: "inspection_in_progress", label: "Inspect", icon: "fact_check", accent: "orange" },
  { key: "rebuild_queued", label: "Rebuild", icon: "build", accent: "orange" },
  { key: "rebuilt_verified_boxed", label: "Boxed", icon: "inventory_2", accent: "green" },
  { key: "scrapped_parts_banked", label: "Scrapped", icon: "delete_forever", accent: "red" },
  { key: "denied", label: "Denied", icon: "block", accent: "red" },
  { key: "closed", label: "Closed", icon: "verified", accent: "green" },
];

const primaryActionSx = {
  bgcolor: "#B54708",
  color: "#fff",
  borderRadius: 1,
  minHeight: 44,
  fontWeight: 900,
  textTransform: "none",
  boxShadow: "none",
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  "&:hover": { bgcolor: "#953A05", boxShadow: "none" },
  "&.Mui-disabled": { bgcolor: "rgba(181,71,8,0.18)", color: "rgba(15,15,18,0.44)" },
};

const successActionSx = {
  bgcolor: iosColors.green,
  color: "#fff",
  borderRadius: 1,
  minHeight: 44,
  fontWeight: 900,
  textTransform: "none",
  boxShadow: "none",
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  "&:hover": { bgcolor: "#0D6149", boxShadow: "none" },
  "&.Mui-disabled": { bgcolor: "rgba(20,122,92,0.18)", color: "rgba(15,15,18,0.44)" },
};

const secondaryActionSx = {
  bgcolor: "rgba(255,106,0,0.07)",
  color: "#8F3700",
  borderColor: "rgba(181,71,8,0.38)",
  borderRadius: 1,
  minHeight: 44,
  fontWeight: 900,
  textTransform: "none",
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  "&:hover": { bgcolor: "rgba(255,106,0,0.12)", borderColor: "rgba(181,71,8,0.52)" },
};

const dangerActionSx = {
  bgcolor: "rgba(215,38,30,0.07)",
  color: "#A51812",
  borderColor: "rgba(215,38,30,0.38)",
  borderRadius: 1,
  minHeight: 44,
  fontWeight: 900,
  textTransform: "none",
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  "&:hover": { bgcolor: "rgba(215,38,30,0.12)", borderColor: "rgba(215,38,30,0.52)" },
};

const iconActionSx = {
  bgcolor: "rgba(255,106,0,0.07)",
  color: "#8F3700",
  border: "1px solid rgba(181,71,8,0.24)",
  "&:hover": { bgcolor: "rgba(255,106,0,0.12)" },
};

const stickyFooterSx = {
  position: "fixed",
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 20,
  px: 1,
  pt: 0.8,
  pb: "calc(env(safe-area-inset-bottom) + 0.8rem)",
  bgcolor: "rgba(247,248,250,0.96)",
  borderTop: `1px solid ${iosColors.line}`,
  backdropFilter: "blur(14px)",
};

const pretty = (value, fallback = "-") => {
  const text = String(value || "").trim();
  if (!text) return fallback;
  return text.replaceAll("_", " ").replace(/\b\w/g, (m) => m.toUpperCase());
};

const compact = (value, fallback = "-") => {
  const text = String(value || "").trim();
  return text || fallback;
};

const normalizeEntity = (row) => {
  if (!row) return null;
  if (row?.data) return normalizeEntity(row.data);
  if (row?.attributes) return { id: row.id, documentId: row.documentId || row.id, ...row.attributes };
  return row;
};

const dateText = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleDateString();
};

const dateTimeText = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? String(value) : d.toLocaleString();
};

const parseCodes = (value) =>
  String(value || "")
    .split(",")
    .map((entry) => entry.trim())
    .filter(Boolean);

const imageRows = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);

const uploadFiles = async (fileList) => {
  const files = Array.from(fileList || []).filter(Boolean);
  if (!files.length) return [];
  const formData = new FormData();
  files.forEach((file) => formData.append("files", file));
  const response = await api.post("/api/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
  return (Array.isArray(response?.data) ? response.data : [])
    .map((row) => Number(row?.id || 0))
    .filter((id) => Number.isFinite(id) && id > 0);
};

const emptyAddress = () => ({
  name: "",
  street1: "",
  street2: "",
  city: "",
  state: "",
  zip: "",
  country: "",
  phone: "",
  email: "",
});

const addressValue = (value) => ({
  ...emptyAddress(),
  ...(value && typeof value === "object" ? value : {}),
});

const rateKey = (rate) =>
  [String(rate?.quoteId || "").trim(), String(rate?.carrier || "").trim().toLowerCase(), String(rate?.service || "").trim().toLowerCase()].join("|");

const formatMoney = (amount, currency) => {
  const value = Number(amount || 0);
  const normalizedCurrency = String(currency || "USD").trim().toUpperCase() || "USD";
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: normalizedCurrency, maximumFractionDigits: 2 }).format(value);
  } catch {
    return `${normalizedCurrency} ${value.toFixed(2)}`;
  }
};

const workflowSteps = (claim) => {
  const row = claim || {};
  const returnRequest = row?.returnRequest || {};
  return [
    { key: "submitted", label: "Claim submitted", at: row?.submittedAt || row?.createdAt },
    { key: "awaiting_claim_decision", label: "Shipping requested / awaiting approval", at: row?.shippingOptionRequestedAt || row?.submittedAt },
    { key: "inbound_label_generated", label: "Inbound label generated", at: returnRequest?.updatedAt || row?.approvedAt },
    { key: "replacement_order_created", label: "Replacement order created", at: row?.replacementApprovalAt || row?.approvedAt },
    { key: "replacement_shipped", label: "Replacement shipped", at: row?.outboundShippedAt },
    { key: "awaiting_replacement_outcome", label: "Replacement in field / awaiting outcome", at: row?.outboundDeliveredAt || row?.outboundShippedAt },
    { key: "received_for_inspection", label: "Returned unit received", at: row?.returnReceivedAt },
    { key: "inspection_in_progress", label: "Inspection in progress", at: row?.inspectionCompletedAt || returnRequest?.warehouseInspectedAt },
    { key: "rebuild_queued", label: "Rebuild queued", at: row?.inspectionCompletedAt },
    { key: "rebuilt_verified_boxed", label: "Rebuilt, verified, boxed", at: row?.closedAt || row?.updatedAt },
    { key: "scrapped_parts_banked", label: "Scrapped", at: row?.closedAt || row?.inspectionCompletedAt },
    { key: "denied", label: "Claim denied", at: row?.deniedAt || row?.claimDecisionAt },
    { key: "closed", label: "Claim closed", at: row?.closedAt },
  ];
};

const stageAccent = (stage, workflow, status) => {
  const normalized = String(stage || status || "").trim().toLowerCase();
  if (workflow?.repeatUnit || normalized === "denied") return "red";
  if (["rebuilt_verified_boxed", "scrapped_parts_banked", "closed", "approved", "inbound_label_generated", "replacement_order_created", "replacement_shipped"].includes(normalized)) return "green";
  return "orange";
};

export function WarrantyClaimsMobile() {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState("all");
  const [workflowStage, setWorkflowStage] = useState("all");
  const [search, setSearch] = useState("");
  const [submittedSearch, setSubmittedSearch] = useState("");

  useEffect(() => {
    const t = window.setTimeout(() => setSubmittedSearch(search.trim()), 250);
    return () => window.clearTimeout(t);
  }, [search]);

  const query = useQuery({
    queryKey: ["m-staff-warranty-claims", statusFilter, workflowStage, submittedSearch],
    queryFn: () =>
      get("/api/storefront/staff/warranty-claims", {
        status: statusFilter,
        workflowStage,
        search: submittedSearch,
      }),
    staleTime: 10_000,
  });

  const rows = useMemo(() => (Array.isArray(query.data?.data) ? query.data.data : []), [query.data]);
  const summary = query.data?.meta?.summary || {};

  return (
    <IosPage>
      <MobileTopBar title="Warranty" subtitle={query.isFetching ? "Syncing..." : "Claims"} onBack={() => navigate("/m/dashboard")} />
      <Stack spacing={1.05} sx={{ mt: 1 }}>
        <IosSectionHeader eyebrow="Warranty" title="Claims workflow" subtitle="Approvals, inbound labels, replacements, inspection, rebuild, and parts bank disposition." />
        <IosFilterBar label="Status" value={statusFilter} options={STATUS_OPTIONS} onChange={setStatusFilter} />
        <IosFilterBar label="Workflow" value={workflowStage} options={WORKFLOW_OPTIONS} onChange={setWorkflowStage} />
        <TextField
          fullWidth
          placeholder="Search RMA, tag, customer, order"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") setSubmittedSearch(search.trim());
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon fontSize="small" sx={{ color: iosColors.subtle }}>search</Icon>
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label="Clear search"
                  onClick={() => setSearch("")}
                  sx={{ color: "#8F3700", bgcolor: "rgba(255,106,0,0.07)", "&:hover": { bgcolor: "rgba(255,106,0,0.12)" } }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
          <MetricChip label={`Total ${summary.total || rows.length}`} />
          <MetricChip label={`Decision ${summary.needsDecision || 0}`} tone="orange" />
          <MetricChip label={`Inspection ${summary.needsInspection || 0}`} tone="green" />
          <MetricChip label={`Outcome ${summary.needsReplacementOutcome || 0}`} tone="orange" />
          <MetricChip label={`Repeat ${summary.repeatUnit || 0}`} tone="red" />
        </Stack>
        {query.isLoading ? <LinearProgress sx={{ borderRadius: 1 }} /> : null}
        {query.isError ? (
          <Typography sx={{ color: iosColors.red, fontSize: 13, fontWeight: 850 }}>
            {query.error?.response?.data?.error?.message || query.error?.message || "Unable to load warranty claims."}
          </Typography>
        ) : null}
        <IosList>
          {rows.map((claim) => {
            const workflow = claim.workflow || {};
            const unit = claim.unit || {};
            const customer = claim.claimantCustomer || {};
            const replacementOrder = claim.replacementSalesOrder || {};
            const documentId = claim.documentId;
            return (
              <IosListItem
                key={documentId || claim.id}
                icon="verified_user"
                title={claim.rmaNumber || documentId || "Warranty claim"}
                meta={[
                  customer.name || "Unknown customer",
                  unit.optionalTag || unit.serialNumber || "No tag",
                  pretty(claim.requestType),
                  replacementOrder.salesOrderNumber ? `Order ${replacementOrder.salesOrderNumber}` : null,
                ].filter(Boolean).join(" • ")}
                trailing={pretty(claim.workflowStage || claim.status)}
                accent={stageAccent(claim.workflowStage, workflow, claim.status)}
                onClick={() => {
                  if (documentId) navigate(`/m/sales/warranty-claims/${documentId}`);
                }}
              />
            );
          })}
          {!rows.length && !query.isLoading ? (
            <Typography sx={{ color: iosColors.muted, fontSize: 13, px: 1.2, py: 1.4 }}>No warranty claims match this view.</Typography>
          ) : null}
        </IosList>
      </Stack>
    </IosPage>
  );
}

export function WarrantyClaimDetailMobile() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [decision, setDecision] = useState("approve");
  const [decisionNotes, setDecisionNotes] = useState("");
  const [inspectionDisposition, setInspectionDisposition] = useState("rebuildable");
  const [inspectionNotes, setInspectionNotes] = useState("");
  const [replacementOutcomeNotes, setReplacementOutcomeNotes] = useState("");
  const [followUpIssueCodes, setFollowUpIssueCodes] = useState("");
  const [followUpIssueDescription, setFollowUpIssueDescription] = useState("");
  const [followUpNotes, setFollowUpNotes] = useState("");
  const [notice, setNotice] = useState(null);
  const [shippingDialogOpen, setShippingDialogOpen] = useState(false);
  const [shippingDialogMode, setShippingDialogMode] = useState("approve");
  const [shippingDialogError, setShippingDialogError] = useState("");
  const [shippingQuote, setShippingQuote] = useState(null);
  const [shippingRequestDocumentId, setShippingRequestDocumentId] = useState("");
  const [shippingForm, setShippingForm] = useState({
    bookingMode: "pickup_plus_label",
    originAddress: emptyAddress(),
    destinationAddress: emptyAddress(),
    selectedRateKey: "",
    packages: [],
  });

  const query = useQuery({
    queryKey: ["m-staff-warranty-claim", documentId],
    queryFn: () => get(`/api/storefront/staff/warranty-claims/${documentId}`),
    enabled: !!documentId,
    staleTime: 8_000,
  });

  const claim = query.data?.data?.claim || null;
  const workflow = query.data?.data?.workflow || {};
  const unit = claim?.unit || {};
  const customer = claim?.claimantCustomer || {};
  const claimantUser = claim?.claimantUser || {};
  const returnRequest = claim?.returnRequest || {};
  const replacementOrder = claim?.replacementSalesOrder || {};
  const replacementUnit = claim?.replacementUnit || {};
  const supportThread = claim?.supportThread || {};
  const parentClaim = claim?.parentWarrantyClaim || {};
  const childClaims = Array.isArray(claim?.childWarrantyClaims) ? claim.childWarrantyClaims : [];
  const returnRequestDocumentId = String(returnRequest?.documentId || "").trim();
  const evidenceImages = useMemo(() => imageRows(claim?.customerEvidenceImages), [claim?.customerEvidenceImages]);
  const inspectionImages = useMemo(() => imageRows(returnRequest?.warehouseInspectionImages), [returnRequest?.warehouseInspectionImages]);
  const workflowAuditLog = useMemo(() => {
    const rows = Array.isArray(claim?.workflowAuditLog) ? claim.workflowAuditLog : [];
    return rows
      .filter((row) => row && typeof row === "object")
      .sort((a, b) => Date.parse(String(b?.at || "")) - Date.parse(String(a?.at || "")));
  }, [claim?.workflowAuditLog]);

  useEffect(() => {
    if (!claim) return;
    const disposition = String(claim.inspectionDisposition || "").trim();
    if (disposition && disposition !== "pending") setInspectionDisposition(disposition);
    setInspectionNotes(returnRequest?.warehouseInspectionNotes || "");
  }, [claim, returnRequest?.warehouseInspectionNotes]);

  const action = useMutation({
    mutationFn: ({ endpoint, payload }) => post(endpoint, { data: payload || {} }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["m-staff-warranty-claim", documentId] });
      await qc.invalidateQueries({ queryKey: ["m-staff-warranty-claims"] });
      query.refetch();
    },
  });

  const shippingQuoteMutation = useMutation({
    mutationFn: ({ requestDocumentId, payload }) =>
      post(`/api/storefront/staff/return-requests/${requestDocumentId}/shipping/quote`, { data: payload || {} }),
  });

  const runAction = async (endpoint, payload, successText) => {
    try {
      setNotice(null);
      await action.mutateAsync({ endpoint, payload });
      setNotice({ type: "success", text: successText });
      return true;
    } catch (error) {
      setNotice({ type: "error", text: error?.response?.data?.error?.message || error?.message || "Workflow action failed." });
      return false;
    }
  };

  const hydrateShippingQuote = (payload) => {
    const data = payload?.data || payload || {};
    const shipping = data?.shipping || {};
    const rates = Array.isArray(shipping?.rates) ? shipping.rates : [];
    const preferredRate = shipping?.selectedRate || rates[0] || null;
    setShippingQuote(data);
    setShippingForm((current) => ({
      ...current,
      bookingMode: String(shipping?.bookingMode || current.bookingMode || "pickup_plus_label"),
      originAddress: addressValue(shipping?.originAddress),
      destinationAddress: addressValue(shipping?.destinationAddress),
      packages: Array.isArray(shipping?.packages) ? shipping.packages : [],
      selectedRateKey: preferredRate ? rateKey(preferredRate) : "",
    }));
  };

  const requestShippingQuote = async (requestDocumentId, payload) => {
    setShippingDialogError("");
    const response = await shippingQuoteMutation.mutateAsync({
      requestDocumentId,
      payload: { ...(payload || {}), debug: true },
    });
    hydrateShippingQuote(response);
    return response;
  };

  const openShippingDialog = async (mode) => {
    let targetReturnRequestDocumentId = returnRequestDocumentId;
    if (!targetReturnRequestDocumentId) {
      const refreshed = await query.refetch();
      targetReturnRequestDocumentId = String(refreshed?.data?.data?.claim?.returnRequest?.documentId || "").trim();
    }
    if (!targetReturnRequestDocumentId) {
      setNotice({ type: "error", text: "This warranty claim is missing its linked return request, so shipping cannot be quoted yet." });
      return;
    }
    setNotice(null);
    setShippingRequestDocumentId(targetReturnRequestDocumentId);
    setShippingDialogMode(mode);
    setShippingDialogOpen(true);
    setShippingQuote(null);
    setShippingDialogError("");
    const bookingMode = String(workflow?.requestedShippingBookingMode || claim?.requestedShippingBookingMode || "pickup_plus_label");
    try {
      await requestShippingQuote(targetReturnRequestDocumentId, { bookingMode });
    } catch (error) {
      setShippingDialogError(error?.response?.data?.error?.message || error?.message || "Unable to load shipping quote.");
    }
  };

  const updateShippingAddress = (addressKey, field, value) => {
    setShippingForm((current) => ({
      ...current,
      [addressKey]: {
        ...addressValue(current?.[addressKey]),
        [field]: value,
      },
    }));
  };

  const quoteRates = Array.isArray(shippingQuote?.shipping?.rates) ? shippingQuote.shipping.rates : [];
  const selectedRate =
    quoteRates.find((rate) => rateKey(rate) === shippingForm.selectedRateKey) ||
    (shippingQuote?.shipping?.selectedRate && rateKey(shippingQuote.shipping.selectedRate) === shippingForm.selectedRateKey
      ? shippingQuote.shipping.selectedRate
      : null);

  const canApproveOrDeny = workflow?.needsDecision && !["denied", "closed"].includes(String(claim?.status || "").toLowerCase());
  const canReceive = !claim?.returnReceivedAt && !["denied", "closed"].includes(String(claim?.status || "").toLowerCase());
  const canRetryLabel = !!returnRequestDocumentId && !["denied", "closed"].includes(String(claim?.status || "").toLowerCase());
  const canConfirmReplacementOutcome = !!workflow?.needsReplacementOutcome && !action.isPending && !!replacementUnit?.documentId;
  const canCreateFollowUp = !!workflow?.needsReplacementOutcome && !action.isPending && !!replacementUnit?.documentId;
  const hasInspectionImages = inspectionImages.length > 0;

  const submitShippingDialog = async () => {
    if (!selectedRate?.carrier || !selectedRate?.service) {
      setShippingDialogError("Select a quoted shipping rate before continuing.");
      return;
    }
    const payload = {
      bookingMode: shippingForm.bookingMode,
      originAddress: shippingForm.originAddress,
      destinationAddress: shippingForm.destinationAddress,
      carrier: selectedRate.carrier,
      service: selectedRate.service,
      quoteId: selectedRate.quoteId || null,
      packages: shippingForm.packages,
    };
    const ok =
      shippingDialogMode === "approve"
        ? await runAction(
            `/api/storefront/staff/warranty-claims/${documentId}/decision`,
            { decision: "approve", notes: decisionNotes, ...payload },
            "Claim approved. Shipping label and replacement workflow updated."
          )
        : await runAction(
            `/api/storefront/staff/return-requests/${shippingRequestDocumentId || returnRequestDocumentId}/shipping/retry`,
            { ...payload, force: true },
            "Inbound shipping label generated."
          );
    if (ok) setShippingDialogOpen(false);
  };

  if (query.isLoading) {
    return (
      <IosPage>
        <LinearProgress sx={{ borderRadius: 1 }} />
      </IosPage>
    );
  }

  if (query.isError || !claim) {
    return (
      <IosPage>
        <MobileTopBar title="Warranty" onBack={() => navigate("/m/sales/warranty-claims")} />
        <IosCard sx={{ mt: 1 }}>
          <Typography sx={{ color: iosColors.red, fontSize: 13, fontWeight: 850 }}>Unable to load warranty claim.</Typography>
        </IosCard>
      </IosPage>
    );
  }

  return (
    <IosPage>
      <MobileTopBar
        title={claim.rmaNumber || "Warranty Claim"}
        subtitle={pretty(claim.workflowStage || claim.status)}
        onBack={() => navigate("/m/sales/warranty-claims")}
        right={
          supportThread?.documentId ? (
            <IconButton aria-label="Claim chat" size="small" onClick={() => navigate(`/m/sales/warranty-claims/${documentId}/chat`)} sx={iconActionSx}>
              <Icon fontSize="small">chat</Icon>
            </IconButton>
          ) : null
        }
      />
      <Stack spacing={1.05} sx={{ mt: 1, pb: 3 }}>
        <IosSectionHeader eyebrow="Warranty" title="Claim workflow" subtitle="Approval, inbound return, replacement order, inspection, and disposition." />

        {notice ? (
          <IosCard sx={{ borderColor: notice.type === "success" ? "rgba(20,122,92,0.35)" : "rgba(215,38,30,0.35)" }}>
            <Typography sx={{ color: notice.type === "success" ? iosColors.green : iosColors.red, fontSize: 13, fontWeight: 850 }}>
              {notice.text}
            </Typography>
          </IosCard>
        ) : null}

        <IosCard>
          <Stack spacing={0.75}>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              <MetricChip label={pretty(claim.workflowStage || claim.status)} tone={stageAccent(claim.workflowStage, workflow, claim.status)} />
              {workflow.needsDecision ? <MetricChip label="Needs decision" tone="orange" /> : null}
              {workflow.needsInspection ? <MetricChip label="Needs inspection" tone="green" /> : null}
              {workflow.needsReplacementOutcome ? <MetricChip label="Needs outcome" tone="orange" /> : null}
              {workflow.manualReviewRequired ? <MetricChip label="Manual review" tone="red" /> : null}
              {workflow.repeatUnit ? <MetricChip label="Repeat unit" tone="red" /> : null}
            </Stack>
            <InfoRow label="Customer" value={customer.name || "-"} />
            <InfoRow label="Claimant" value={claimantUser.email || claimantUser.username || "-"} />
            <InfoRow label="Unit" value={unit.optionalTag || unit.serialNumber || unit.documentId || "-"} />
            <InfoRow label="Transmission" value={unit.transmissionModel || "-"} />
            <InfoRow label="Request" value={`${pretty(claim.requestType)} • Sequence #${Number(claim.claimSequence || 1)}`} />
            <InfoRow label="Submitted" value={dateText(claim.submittedAt || claim.createdAt)} />
            <InfoRow label="Vehicle" value={[claim.vehicleYear, claim.vehicleMake, claim.vehicleModel, claim.vehicleTrim].filter(Boolean).join(" ") || "-"} />
            <InfoRow label="VIN" value={claim.vehicleVin || "-"} />
            <InfoRow label="Shipping" value={pretty(workflow.requestedShippingBookingMode || claim.requestedShippingBookingMode)} />
            <InfoRow label="Outcome" value={pretty(claim.replacementOutcomeStatus || "not_required")} />
            <Typography sx={{ color: iosColors.ink, fontSize: 13, lineHeight: 1.35, whiteSpace: "pre-wrap" }}>
              {claim.issueDescription || claim.reason || "No issue description."}
            </Typography>
            {workflow.manualReviewRequired ? <NoticeText tone="orange" text={claim.supportMessage || "This claim requires manual review before replacement decisions are made."} /> : null}
            {workflow.repeatUnit ? <NoticeText tone="red" text="Same tag returned again. Inspection should end in scrap / parts bank." /> : null}
            {parentClaim?.documentId ? <NoticeText tone="green" text={`Follow-up claim linked to parent ${parentClaim.rmaNumber || parentClaim.documentId}.`} /> : null}
          </Stack>
        </IosCard>

        <IosCard>
          <Typography sx={{ color: iosColors.ink, fontSize: 15.5, fontWeight: 900, mb: 0.85 }}>Workflow</Typography>
          <Stack spacing={0.65}>
            {workflowSteps(claim).map((step) => {
              const active = String(claim.workflowStage || "").trim().toLowerCase() === step.key;
              const complete = !!step.at;
              return (
                <Stack key={step.key} direction="row" spacing={0.8} alignItems="flex-start">
                  <Icon sx={{ color: active || complete ? iosColors.green : iosColors.subtle, fontSize: 19, mt: 0.05 }}>
                    {active || complete ? "check_circle" : "radio_button_unchecked"}
                  </Icon>
                  <Typography sx={{ color: iosColors.muted, fontSize: 12.6, lineHeight: 1.25 }}>
                    {step.label}{step.at ? ` • ${dateTimeText(step.at)}` : ""}
                  </Typography>
                </Stack>
              );
            })}
          </Stack>
        </IosCard>

        <IosCard>
          <Stack spacing={0.85}>
            <Typography sx={{ color: iosColors.orange, fontSize: 12, fontWeight: 900, textTransform: "uppercase" }}>Decision + Label</Typography>
            <TextField select size="small" label="Decision" value={decision} onChange={(e) => setDecision(e.target.value)}>
              <MenuItem value="approve">Approve claim</MenuItem>
              <MenuItem value="deny">Deny claim</MenuItem>
            </TextField>
            <TextField size="small" label="Decision notes" value={decisionNotes} onChange={(e) => setDecisionNotes(e.target.value)} multiline minRows={2} />
            <Stack spacing={0.75}>
              <Button
                variant={decision === "deny" ? "outlined" : "contained"}
                disabled={!canApproveOrDeny || action.isPending}
                sx={decision === "deny" ? dangerActionSx : primaryActionSx}
                onClick={() => {
                  if (decision === "deny") {
                    runAction(`/api/storefront/staff/warranty-claims/${documentId}/decision`, { decision, notes: decisionNotes }, "Claim denied.");
                    return;
                  }
                  openShippingDialog("approve");
                }}
              >
                {decision === "deny" ? "Deny Claim" : "Approve + Generate Label"}
              </Button>
              <Button
                variant="outlined"
                disabled={!canRetryLabel || action.isPending}
                startIcon={<Icon>local_shipping</Icon>}
                sx={secondaryActionSx}
                onClick={() => openShippingDialog("label")}
              >
                Generate / Regenerate Inbound Label
              </Button>
            </Stack>
          </Stack>
        </IosCard>

        <IosCard>
          <Stack spacing={0.85}>
            <Typography sx={{ color: iosColors.orange, fontSize: 12, fontWeight: 900, textTransform: "uppercase" }}>Return + Replacement</Typography>
            <InfoRow label="Return status" value={pretty(returnRequest.status || returnRequest.shippingProviderStatus)} />
            <InfoRow label="Carrier" value={[returnRequest.shippingCarrier, returnRequest.trackingNumber].filter(Boolean).join(" • ") || "-"} />
            <InfoRow label="Replacement order" value={replacementOrder.salesOrderNumber || replacementOrder.documentId || "Not created"} />
            <InfoRow label="Replacement approval" value={pretty(claim.replacementApprovalStatus || "not_required")} />
            <InfoRow label="Replacement unit" value={replacementUnit.optionalTag || replacementUnit.serialNumber || replacementUnit.documentId || "-"} />
            <InfoRow label="Replacement outcome" value={`${pretty(claim.replacementOutcomeStatus || "not_required")}${claim.replacementOutcomeAt ? ` • ${dateTimeText(claim.replacementOutcomeAt)}` : ""}`} />
            {claim.replacementOutcomeNotes ? <ReadOnlyText label="Replacement outcome notes" value={claim.replacementOutcomeNotes} /> : null}
            <Divider />
            <Button
              variant="outlined"
              startIcon={<Icon>move_to_inbox</Icon>}
              disabled={!canReceive || action.isPending}
              sx={secondaryActionSx}
              onClick={() => runAction(`/api/storefront/staff/warranty-claims/${documentId}/receive`, {}, "Returned unit marked received and queued for inspection.")}
            >
              Mark Unit Received
            </Button>
            {replacementOrder.documentId ? (
              <Button variant="outlined" startIcon={<Icon>receipt_long</Icon>} sx={secondaryActionSx} onClick={() => navigate(`/m/sales/${replacementOrder.documentId}`)}>
                Open Linked Warranty Order
              </Button>
            ) : null}
            {workflow.needsReplacementOutcome ? (
              <>
                <NoticeText tone="green" text="Keep the original claim open until the returned unit disposition and the replacement outcome are resolved." />
                <TextField
                  size="small"
                  label="Replacement outcome notes"
                  value={replacementOutcomeNotes}
                  onChange={(event) => setReplacementOutcomeNotes(event.target.value)}
                  multiline
                  minRows={2}
                />
                <Button
                  variant="contained"
                  disabled={!canConfirmReplacementOutcome}
                  sx={successActionSx}
                  onClick={() =>
                    runAction(
                      `/api/storefront/staff/warranty-claims/${documentId}/replacement-outcome`,
                      { outcome: "confirmed_good", notes: replacementOutcomeNotes },
                      "Replacement outcome confirmed."
                    )
                  }
                >
                  Confirm Replacement Good
                </Button>
                <Divider />
                <Typography sx={{ color: iosColors.orange, fontSize: 12, fontWeight: 900, textTransform: "uppercase" }}>Follow-up claim</Typography>
                <TextField size="small" label="Issue codes" value={followUpIssueCodes} onChange={(event) => setFollowUpIssueCodes(event.target.value)} />
                <TextField
                  size="small"
                  label="Issue description"
                  value={followUpIssueDescription}
                  onChange={(event) => setFollowUpIssueDescription(event.target.value)}
                  multiline
                  minRows={3}
                />
                <TextField size="small" label="Internal notes" value={followUpNotes} onChange={(event) => setFollowUpNotes(event.target.value)} multiline minRows={2} />
                <Button
                  variant="contained"
                  disabled={!canCreateFollowUp || !followUpIssueDescription.trim()}
                  sx={primaryActionSx}
                  onClick={() =>
                    runAction(
                      `/api/storefront/staff/warranty-claims/${documentId}/follow-up`,
                      { issueCodes: parseCodes(followUpIssueCodes), issueDescription: followUpIssueDescription, notes: followUpNotes },
                      "Follow-up warranty claim created from the replacement unit."
                    )
                  }
                >
                  Create Follow-Up Claim
                </Button>
              </>
            ) : null}
            {childClaims.length ? (
              <Stack spacing={0.65}>
                <Typography sx={{ color: iosColors.muted, fontSize: 11.5, fontWeight: 900, textTransform: "uppercase" }}>Child claims</Typography>
                {childClaims.map((child) => (
                  <ButtonBase
                    key={child.documentId || child.id}
                    onClick={() => child.documentId && navigate(`/m/sales/warranty-claims/${child.documentId}`)}
                    sx={{ display: "block", width: "100%", textAlign: "left", borderRadius: 1 }}
                  >
                    <Box sx={{ p: 1, border: `1px solid ${iosColors.line}`, borderRadius: 1, bgcolor: "#fff" }}>
                      <Typography sx={{ color: iosColors.ink, fontSize: 13.5, fontWeight: 900 }}>{child.rmaNumber || child.documentId}</Typography>
                      <Typography sx={{ color: iosColors.muted, fontSize: 12.3 }}>{pretty(child.requestType)} • {pretty(child.status)}</Typography>
                    </Box>
                  </ButtonBase>
                ))}
              </Stack>
            ) : null}
          </Stack>
        </IosCard>

        <IosCard>
          <Stack spacing={0.85}>
            <Typography sx={{ color: iosColors.orange, fontSize: 12, fontWeight: 900, textTransform: "uppercase" }}>Inspection</Typography>
            <InfoRow label="Disposition" value={pretty(claim.inspectionDisposition || "pending")} />
            <InfoRow label="Completed" value={dateTimeText(claim.inspectionCompletedAt || returnRequest.warehouseInspectedAt)} />
            {returnRequest.warehouseInspectionNotes ? <ReadOnlyText label="Warehouse notes" value={returnRequest.warehouseInspectionNotes} /> : null}
            {inspectionImages.length ? <ImageLinkList title="Inspection images" images={inspectionImages} /> : <NoticeText tone="orange" text="Inspection photos are required before saving inspection disposition." />}
            <TextField select size="small" label="Inspection decision" value={inspectionDisposition} onChange={(e) => setInspectionDisposition(e.target.value)}>
              <MenuItem value="rebuildable">Rebuildable</MenuItem>
              <MenuItem value="scrap_parts_bank">Scrap / parts bank</MenuItem>
            </TextField>
            <TextField size="small" label="Inspection notes" value={inspectionNotes} onChange={(e) => setInspectionNotes(e.target.value)} multiline minRows={2} />
            <Stack direction="row" spacing={0.75}>
              <Button
                variant="outlined"
                fullWidth
                disabled={!hasInspectionImages || action.isPending}
                sx={secondaryActionSx}
                onClick={() =>
                  runAction(
                    `/api/storefront/staff/warranty-claims/${documentId}/inspection`,
                    { disposition: inspectionDisposition, notes: inspectionNotes },
                    "Inspection saved."
                  )
                }
              >
                Save
              </Button>
              <Button
                variant={inspectionDisposition === "scrap_parts_bank" ? "outlined" : "contained"}
                fullWidth
                disabled={action.isPending}
                sx={inspectionDisposition === "scrap_parts_bank" ? dangerActionSx : successActionSx}
                onClick={() =>
                  runAction(
                    inspectionDisposition === "scrap_parts_bank"
                      ? `/api/storefront/staff/warranty-claims/${documentId}/scrap`
                      : `/api/storefront/staff/warranty-claims/${documentId}/rebuild-complete`,
                    { notes: inspectionNotes },
                    inspectionDisposition === "scrap_parts_bank" ? "Unit scrapped to parts bank." : "Rebuild marked complete."
                  )
                }
              >
                Finish
              </Button>
            </Stack>
            {action.isPending ? <LinearProgress sx={{ borderRadius: 1 }} /> : null}
          </Stack>
        </IosCard>

        <IosCard>
          <Stack spacing={0.85}>
            <Typography sx={{ color: iosColors.ink, fontSize: 15.5, fontWeight: 900 }}>Customer evidence</Typography>
            {evidenceImages.length ? <ImageLinkList title="Evidence images" images={evidenceImages} /> : <NoticeText tone="green" text="No customer evidence images uploaded yet." />}
            {claim.customerUpdateNotes ? <ReadOnlyText label="Latest customer updates" value={claim.customerUpdateNotes} /> : null}
          </Stack>
        </IosCard>

        <IosCard>
          <Stack spacing={0.85}>
            <Typography sx={{ color: iosColors.ink, fontSize: 15.5, fontWeight: 900 }}>Workflow audit</Typography>
            {workflowAuditLog.length ? (
              workflowAuditLog.map((entry, index) => (
                <Box key={`${String(entry?.type || "event")}-${index}`} sx={{ p: 1, border: `1px solid ${iosColors.line}`, borderRadius: 1, bgcolor: "#fff" }}>
                  <Typography sx={{ color: iosColors.ink, fontSize: 13.5, fontWeight: 900 }}>{pretty(entry.type || "event")}</Typography>
                  <Typography sx={{ color: iosColors.muted, fontSize: 12.25 }}>{dateTimeText(entry.at)}</Typography>
                  {entry.bookingMode ? <Typography sx={{ color: iosColors.muted, fontSize: 12.25 }}>Shipping mode: {pretty(entry.bookingMode)}</Typography> : null}
                  {entry.salesOrderNumber ? <Typography sx={{ color: iosColors.muted, fontSize: 12.25 }}>Order: {entry.salesOrderNumber}</Typography> : null}
                  {entry.notes ? <Typography sx={{ color: iosColors.ink, fontSize: 12.5, whiteSpace: "pre-wrap", mt: 0.35 }}>{entry.notes}</Typography> : null}
                </Box>
              ))
            ) : (
              <Typography sx={{ color: iosColors.muted, fontSize: 13 }}>No workflow audit entries yet.</Typography>
            )}
          </Stack>
        </IosCard>
      </Stack>

      <ShippingDialog
        open={shippingDialogOpen}
        mode={shippingDialogMode}
        form={shippingForm}
        quoteRates={quoteRates}
        selectedRate={selectedRate}
        selectedRateKey={shippingForm.selectedRateKey}
        quote={shippingQuote}
        error={shippingDialogError}
        loading={shippingQuoteMutation.isPending}
        saving={action.isPending}
        onClose={() => (action.isPending || shippingQuoteMutation.isPending ? null : setShippingDialogOpen(false))}
        onChangeForm={setShippingForm}
        onAddressChange={updateShippingAddress}
        onRequote={() =>
          requestShippingQuote(shippingRequestDocumentId || returnRequestDocumentId, {
            bookingMode: shippingForm.bookingMode,
            originAddress: shippingForm.originAddress,
            destinationAddress: shippingForm.destinationAddress,
            packages: shippingForm.packages,
            carrier: selectedRate?.carrier || null,
            service: selectedRate?.service || null,
            quoteId: selectedRate?.quoteId || null,
            debug: true,
          }).catch((error) => {
            setShippingDialogError(error?.response?.data?.error?.message || error?.message || "Unable to refresh shipping quote.");
          })
        }
        onSubmit={submitShippingDialog}
      />
    </IosPage>
  );
}

export function WarrantyClaimChatMobile() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState(null);

  const claimQuery = useQuery({
    queryKey: ["m-staff-warranty-claim-chat-context", documentId],
    queryFn: () => get(`/api/storefront/staff/warranty-claims/${documentId}`),
    enabled: !!documentId,
    staleTime: 8_000,
  });

  const claim = useMemo(() => normalizeEntity(claimQuery.data?.data?.claim), [claimQuery.data]);
  const unit = normalizeEntity(claim?.unit) || {};
  const customer = normalizeEntity(claim?.claimantCustomer) || {};
  const claimantUser = normalizeEntity(claim?.claimantUser) || {};
  const supportThread = normalizeEntity(claim?.supportThread) || {};
  const threadDocumentId = String(supportThread?.documentId || "").trim();

  const chatQuery = useQuery({
    queryKey: ["m-staff-warranty-claim-chat-thread", threadDocumentId],
    queryFn: () => get(`/api/support-chat/threads/${threadDocumentId}`),
    enabled: !!threadDocumentId,
    staleTime: 3_000,
    refetchInterval: 10_000,
  });

  const messages = useMemo(() => (Array.isArray(chatQuery.data?.data?.messages) ? chatQuery.data.data.messages : []), [chatQuery.data]);

  useEffect(() => {
    if (!threadDocumentId) return;
    post(`/api/support-chat/threads/${threadDocumentId}/read`, { data: {} }).catch(() => {});
  }, [threadDocumentId]);

  const sendMessage = async () => {
    const trimmed = message.trim();
    if (!threadDocumentId || (!trimmed && !files.length)) return;
    try {
      setSaving(true);
      setNotice(null);
      const imageIds = await uploadFiles(files);
      await post(`/api/support-chat/threads/${threadDocumentId}/messages`, {
        data: {
          message: trimmed || `Uploaded ${imageIds.length} image${imageIds.length === 1 ? "" : "s"}.`,
          imageIds,
        },
      });
      setMessage("");
      setFiles([]);
      await chatQuery.refetch();
      await qc.invalidateQueries({ queryKey: ["m-staff-warranty-claim", documentId] });
      await qc.invalidateQueries({ queryKey: ["m-staff-warranty-claims"] });
    } catch (error) {
      setNotice({ type: "error", text: error?.response?.data?.error?.message || error?.message || "Unable to send message." });
    } finally {
      setSaving(false);
    }
  };

  if (claimQuery.isLoading) {
    return (
      <IosPage>
        <LinearProgress sx={{ borderRadius: 1 }} />
      </IosPage>
    );
  }

  if (claimQuery.isError || !claim) {
    return (
      <IosPage>
        <MobileTopBar title="Warranty Chat" onBack={() => navigate(`/m/sales/warranty-claims/${documentId}`)} />
        <IosCard sx={{ mt: 1, borderColor: "rgba(215,38,30,0.35)" }}>
          <Typography sx={{ color: iosColors.red, fontSize: 13, fontWeight: 850 }}>Unable to load the warranty claim chat.</Typography>
        </IosCard>
      </IosPage>
    );
  }

  return (
    <IosPage>
      <MobileTopBar
        title={claim.rmaNumber ? `${claim.rmaNumber} Chat` : "Warranty Chat"}
        subtitle={pretty(supportThread.status || claim.status || "open")}
        onBack={() => navigate(`/m/sales/warranty-claims/${documentId}`)}
        right={
          unit.documentId ? (
            <IconButton aria-label="Open unit" size="small" onClick={() => navigate(`/m/valve-bodies/${unit.documentId}`)} sx={iconActionSx}>
              <Icon fontSize="small">precision_manufacturing</Icon>
            </IconButton>
          ) : null
        }
      />
      <Stack spacing={1.05} sx={{ mt: 1, pb: 19 }}>
        <IosSectionHeader eyebrow="Warranty" title="Claim conversation" subtitle="Message the customer and attach claim photos." />

        {notice ? (
          <IosCard sx={{ borderColor: notice.type === "success" ? "rgba(20,122,92,0.35)" : "rgba(215,38,30,0.35)" }}>
            <Typography sx={{ color: notice.type === "success" ? iosColors.green : iosColors.red, fontSize: 13, fontWeight: 850 }}>
              {notice.text}
            </Typography>
          </IosCard>
        ) : null}

        <IosCard>
          <Stack spacing={0.75}>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              <MetricChip label={pretty(claim.workflowStage || claim.status)} tone={stageAccent(claim.workflowStage, claim.workflow || {}, claim.status)} />
              <MetricChip label={pretty(supportThread.status || "open")} tone={String(supportThread.status || "").toLowerCase() === "resolved" ? "green" : "orange"} />
            </Stack>
            <InfoRow label="Customer" value={customer.name || claimantUser.email || claimantUser.username || "-"} />
            <InfoRow label="Unit" value={unit.optionalTag || unit.serialNumber || unit.documentId || "-"} />
            <InfoRow label="Transmission" value={unit.transmissionModel || "-"} />
            <ReadOnlyText label="Issue" value={claim.issueDescription || claim.reason || "No issue description."} />
          </Stack>
        </IosCard>

        {!threadDocumentId ? (
          <IosCard sx={{ borderColor: "rgba(181,71,8,0.32)" }}>
            <NoticeText tone="orange" text="This claim does not have a support chat thread yet." />
          </IosCard>
        ) : null}

        {chatQuery.isLoading ? <LinearProgress sx={{ borderRadius: 1 }} /> : null}
        {chatQuery.isError ? (
          <IosCard sx={{ borderColor: "rgba(215,38,30,0.35)" }}>
            <Typography sx={{ color: iosColors.red, fontSize: 13, fontWeight: 850 }}>
              {chatQuery.error?.response?.data?.error?.message || chatQuery.error?.message || "Unable to load messages."}
            </Typography>
          </IosCard>
        ) : null}

        <Stack spacing={0.75}>
          {messages.map((row, index) => {
            const fromStaff = String(row?.senderRole || "").toLowerCase() === "staff";
            const mine = Boolean(row?.isMine);
            return (
              <Box
                key={String(row?.documentId || index)}
                sx={{
                  alignSelf: fromStaff ? "flex-end" : "flex-start",
                  width: "fit-content",
                  maxWidth: "92%",
                  p: 1,
                  borderRadius: 1,
                  border: `1px solid ${fromStaff ? "rgba(181,71,8,0.28)" : iosColors.line}`,
                  bgcolor: fromStaff ? "rgba(255,106,0,0.07)" : "#fff",
                }}
              >
                <Typography sx={{ color: fromStaff ? "#8F3700" : iosColors.muted, fontSize: 11.5, fontWeight: 900, lineHeight: 1.25 }}>
                  {fromStaff ? (mine ? "You" : "VBS Team") : "Customer"} • {dateTimeText(row?.createdAt)}
                </Typography>
                <Typography sx={{ mt: 0.35, color: iosColors.ink, fontSize: 13.2, lineHeight: 1.38, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>
                  {row?.message || "Message sent."}
                </Typography>
                {Array.isArray(row?.imageUrls) && row.imageUrls.length ? (
                  <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                    {row.imageUrls.map((url, imageIndex) => (
                      <Button
                        key={`${String(url)}-${imageIndex}`}
                        component="a"
                        href={String(url)}
                        target="_blank"
                        rel="noreferrer"
                        size="small"
                        variant="outlined"
                        startIcon={<Icon>image</Icon>}
                        sx={{ ...secondaryActionSx, minHeight: 34, py: 0.4 }}
                      >
                        Photo {imageIndex + 1}
                      </Button>
                    ))}
                  </Stack>
                ) : null}
              </Box>
            );
          })}
          {!messages.length && !chatQuery.isLoading ? (
            <IosCard>
              <Typography sx={{ color: iosColors.muted, fontSize: 13 }}>No chat messages yet.</Typography>
            </IosCard>
          ) : null}
        </Stack>
      </Stack>

      <Box sx={stickyFooterSx}>
        <Stack spacing={0.65}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={5}
            size="small"
            placeholder="Message customer"
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            sx={{ bgcolor: "#fff" }}
          />
          <Stack direction="row" spacing={0.75}>
            <Button component="label" variant="outlined" fullWidth disabled={saving || !threadDocumentId} sx={secondaryActionSx} startIcon={<Icon>attach_file</Icon>}>
              Photos
              <input hidden type="file" accept="image/*" multiple onChange={(event) => setFiles(Array.from(event.target.files || []))} />
            </Button>
            <Button variant="contained" fullWidth disabled={saving || !threadDocumentId || (!message.trim() && !files.length)} sx={primaryActionSx} onClick={sendMessage}>
              {saving ? "Sending..." : "Send"}
            </Button>
          </Stack>
          {files.length ? (
            <Stack direction="row" spacing={0.6} alignItems="center" justifyContent="space-between">
              <Typography sx={{ color: iosColors.muted, fontSize: 12.3, fontWeight: 800, overflowWrap: "anywhere" }}>
                {files.length} photo{files.length === 1 ? "" : "s"} selected
              </Typography>
              <Button size="small" variant="text" sx={{ color: "#8F3700", fontWeight: 900, textTransform: "none" }} onClick={() => setFiles([])}>
                Clear
              </Button>
            </Stack>
          ) : null}
        </Stack>
      </Box>
    </IosPage>
  );
}

function ShippingDialog({ open, mode, form, quoteRates, selectedRate, selectedRateKey, quote, error, loading, saving, onClose, onChangeForm, onAddressChange, onRequote, onSubmit }) {
  const diagnostics = quote?.shipping?.diagnostics || null;
  return (
    <Dialog open={open} onClose={onClose} fullScreen PaperProps={{ sx: { bgcolor: iosColors.canvas } }}>
      <Box sx={{ px: 1, pt: "env(safe-area-inset-top)", pb: 10, bgcolor: iosColors.canvas, minHeight: "100dvh" }}>
        <MobileTopBar title={mode === "approve" ? "Approve + Label" : "Generate Label"} subtitle="Warranty return shipping" onBack={onClose} />
        <Stack spacing={0.9} sx={{ mt: 1 }}>
          {error ? (
            <IosCard sx={{ borderColor: "rgba(215,38,30,0.35)" }}>
              <Typography sx={{ color: iosColors.red, fontSize: 13, fontWeight: 850 }}>{error}</Typography>
            </IosCard>
          ) : null}
          {loading ? <LinearProgress sx={{ borderRadius: 1 }} /> : null}
          <IosCard>
            <Typography sx={{ color: iosColors.ink, fontSize: 16, fontWeight: 900, mb: 0.85 }}>Booking</Typography>
            <TextField
              select
              fullWidth
              size="small"
              label="Booking mode"
              value={form.bookingMode}
              onChange={(event) => onChangeForm((current) => ({ ...current, bookingMode: event.target.value }))}
            >
              <MenuItem value="pickup_plus_label">Pickup + prepaid label</MenuItem>
              <MenuItem value="label_only">Prepaid label only</MenuItem>
            </TextField>
          </IosCard>
          <IosCard>
            <Typography sx={{ color: iosColors.ink, fontSize: 16, fontWeight: 900, mb: 0.85 }}>Customer origin</Typography>
            <AddressFields value={form.originAddress} onChange={(field, value) => onAddressChange("originAddress", field, value)} />
          </IosCard>
          <IosCard>
            <Typography sx={{ color: iosColors.ink, fontSize: 16, fontWeight: 900, mb: 0.85 }}>Return destination</Typography>
            <AddressFields value={form.destinationAddress} onChange={(field, value) => onAddressChange("destinationAddress", field, value)} />
          </IosCard>
          <IosCard>
            <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center" sx={{ mb: 0.8 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: iosColors.ink, fontSize: 16, fontWeight: 900 }}>Quoted rates</Typography>
                <Typography sx={{ color: iosColors.muted, fontSize: 12.5 }}>Packages: {form.packages.length || 0}</Typography>
              </Box>
              <Button variant="outlined" size="small" disabled={loading || saving} sx={{ ...secondaryActionSx, minHeight: 36 }} onClick={onRequote}>
                Re-Quote
              </Button>
            </Stack>
            <Stack spacing={0.7}>
              {quoteRates.length ? (
                quoteRates.map((rate) => {
                  const key = rateKey(rate);
                  const selected = key === selectedRateKey;
                  return (
                    <ButtonBase
                      key={key}
                      onClick={() => onChangeForm((current) => ({ ...current, selectedRateKey: key }))}
                      sx={{ display: "block", width: "100%", textAlign: "left", borderRadius: 1 }}
                    >
                      <Box
                        sx={{
                          p: 1,
                          border: `1px solid ${selected ? iosColors.green : iosColors.line}`,
                          borderRadius: 1,
                          bgcolor: selected ? "rgba(20,122,92,0.06)" : "#fff",
                          width: "100%",
                        }}
                      >
                        <Stack direction="row" spacing={0.8} alignItems="flex-start">
                          <Icon sx={{ color: selected ? iosColors.green : iosColors.subtle, fontSize: 20 }}>
                            {selected ? "check_circle" : "radio_button_unchecked"}
                          </Icon>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography sx={{ color: iosColors.ink, fontSize: 14, fontWeight: 900 }}>
                              {pretty(rate.carrier)} • {pretty(rate.service)}
                            </Typography>
                            <Typography sx={{ color: iosColors.muted, fontSize: 12.5 }}>
                              {formatMoney(rate.amount, rate.currency)}{rate.deliveryDays ? ` • ${rate.deliveryDays} day${Number(rate.deliveryDays) === 1 ? "" : "s"}` : ""}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    </ButtonBase>
                  );
                })
              ) : (
                <Typography sx={{ color: iosColors.muted, fontSize: 13 }}>No rates loaded yet. Quote the addresses first.</Typography>
              )}
            </Stack>
          </IosCard>
          {diagnostics ? (
            <IosCard>
              <Typography sx={{ color: iosColors.ink, fontSize: 16, fontWeight: 900, mb: 0.8 }}>Envia diagnostics</Typography>
              <InfoRow label="Environment" value={pretty(diagnostics.environment || "unknown")} />
              <InfoRow label="Sort policy" value={pretty(diagnostics.quoteSortPolicy || "unknown")} />
              <InfoRow label="Label" value={[diagnostics?.print?.format, diagnostics?.print?.size].filter(Boolean).join(" • ") || "-"} />
            </IosCard>
          ) : null}
        </Stack>
      </Box>
      <Box sx={stickyFooterSx}>
        <Stack direction="row" spacing={0.75}>
          <Button variant="outlined" fullWidth disabled={loading || saving} sx={secondaryActionSx} onClick={onClose}>
            Cancel
          </Button>
          <Button variant="contained" fullWidth disabled={loading || saving || !selectedRate} sx={primaryActionSx} onClick={onSubmit}>
            {mode === "approve" ? "Approve + Label" : "Generate Label"}
          </Button>
        </Stack>
      </Box>
    </Dialog>
  );
}

function AddressFields({ value, onChange }) {
  const address = addressValue(value);
  return (
    <Stack spacing={0.8}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={0.8}>
        <TextField fullWidth size="small" label="Name" value={address.name} onChange={(event) => onChange("name", event.target.value)} />
        <TextField fullWidth size="small" label="Phone" value={address.phone} onChange={(event) => onChange("phone", event.target.value)} />
      </Stack>
      <TextField fullWidth size="small" label="Street" value={address.street1} onChange={(event) => onChange("street1", event.target.value)} />
      <TextField fullWidth size="small" label="Street 2" value={address.street2} onChange={(event) => onChange("street2", event.target.value)} />
      <Stack direction={{ xs: "column", sm: "row" }} spacing={0.8}>
        <TextField fullWidth size="small" label="City" value={address.city} onChange={(event) => onChange("city", event.target.value)} />
        <TextField fullWidth size="small" label="State" value={address.state} onChange={(event) => onChange("state", event.target.value)} />
      </Stack>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={0.8}>
        <TextField fullWidth size="small" label="ZIP / Postal Code" value={address.zip} onChange={(event) => onChange("zip", event.target.value)} />
        <TextField fullWidth size="small" label="Country" value={address.country} onChange={(event) => onChange("country", event.target.value)} />
      </Stack>
      <TextField fullWidth size="small" label="Email" value={address.email} onChange={(event) => onChange("email", event.target.value)} />
    </Stack>
  );
}

function MetricChip({ label, tone }) {
  const color = tone === "red" ? iosColors.red : tone === "green" ? iosColors.green : tone === "orange" ? "#8F3700" : iosColors.muted;
  const bg = tone === "red" ? "rgba(215,38,30,0.07)" : tone === "green" ? "rgba(20,122,92,0.07)" : tone === "orange" ? "rgba(255,106,0,0.07)" : "#fff";
  return <Chip size="small" label={label} sx={{ fontWeight: 850, bgcolor: bg, color, border: `1px solid ${iosColors.line}` }} />;
}

function NoticeText({ text, tone }) {
  const color = tone === "red" ? iosColors.red : tone === "green" ? iosColors.green : "#8F3700";
  const bg = tone === "red" ? "rgba(215,38,30,0.07)" : tone === "green" ? "rgba(20,122,92,0.07)" : "rgba(255,106,0,0.07)";
  return (
    <Box sx={{ p: 1, borderRadius: 1, bgcolor: bg, border: `1px solid ${iosColors.line}` }}>
      <Typography sx={{ color, fontSize: 12.5, fontWeight: 850, lineHeight: 1.3 }}>{text}</Typography>
    </Box>
  );
}

function ReadOnlyText({ label, value }) {
  return (
    <Box sx={{ p: 1, border: `1px solid ${iosColors.line}`, borderRadius: 1, bgcolor: "#fff" }}>
      <Typography sx={{ color: iosColors.muted, fontSize: 11.5, fontWeight: 900, textTransform: "uppercase" }}>{label}</Typography>
      <Typography sx={{ color: iosColors.ink, fontSize: 12.8, lineHeight: 1.35, whiteSpace: "pre-wrap", mt: 0.35 }}>{value}</Typography>
    </Box>
  );
}

function ImageLinkList({ title, images }) {
  return (
    <Stack spacing={0.55}>
      <Typography sx={{ color: iosColors.muted, fontSize: 11.5, fontWeight: 900, textTransform: "uppercase" }}>{title}</Typography>
      {images.map((image, index) => (
        <Button
          key={String(image?.documentId || image?.id || index)}
          component="a"
          href={String(image?.url || "")}
          target="_blank"
          rel="noreferrer"
          variant="outlined"
          startIcon={<Icon>image</Icon>}
          sx={{ ...secondaryActionSx, justifyContent: "flex-start" }}
        >
          {image?.name || `Image ${index + 1}`}
        </Button>
      ))}
    </Stack>
  );
}

function InfoRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
      <Typography sx={{ color: iosColors.muted, fontSize: 12.5, flex: "0 0 auto", maxWidth: "46%" }}>{label}</Typography>
      <Typography sx={{ color: iosColors.ink, fontSize: 12.5, fontWeight: 850, textAlign: "right", minWidth: 0, overflowWrap: "anywhere" }}>
        {value}
      </Typography>
    </Stack>
  );
}

ShippingDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  mode: PropTypes.string.isRequired,
  form: PropTypes.shape({
    bookingMode: PropTypes.string,
    originAddress: PropTypes.shape({}),
    destinationAddress: PropTypes.shape({}),
    selectedRateKey: PropTypes.string,
    packages: PropTypes.arrayOf(PropTypes.shape({})),
  }).isRequired,
  quoteRates: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectedRate: PropTypes.shape({}),
  selectedRateKey: PropTypes.string.isRequired,
  quote: PropTypes.shape({
    shipping: PropTypes.shape({
      diagnostics: PropTypes.shape({}),
    }),
  }),
  error: PropTypes.string,
  loading: PropTypes.bool.isRequired,
  saving: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onChangeForm: PropTypes.func.isRequired,
  onAddressChange: PropTypes.func.isRequired,
  onRequote: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
};
ShippingDialog.defaultProps = { selectedRate: null, quote: null, error: "" };

AddressFields.propTypes = {
  value: PropTypes.shape({}).isRequired,
  onChange: PropTypes.func.isRequired,
};

MetricChip.propTypes = {
  label: PropTypes.string.isRequired,
  tone: PropTypes.oneOf(["orange", "green", "red"]),
};
MetricChip.defaultProps = { tone: "orange" };

NoticeText.propTypes = {
  text: PropTypes.string.isRequired,
  tone: PropTypes.oneOf(["orange", "green", "red"]),
};
NoticeText.defaultProps = { tone: "orange" };

ReadOnlyText.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node.isRequired,
};

ImageLinkList.propTypes = {
  title: PropTypes.string.isRequired,
  images: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

InfoRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
};
InfoRow.defaultProps = { value: "-" };
